// frontend\src\components\analytics\AnalyticsInventoryDashboard.tsx
import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler, ScriptableContext, TooltipItem } from 'chart.js';
import { Line } from 'react-chartjs-2';
import ErrorBoundary from '../ErrorBoundary';
import LoadingSpinner from '../LoadingSpinner';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface InventorySeriesPoint {
  date: string;
  total: number | null;
  byWarehouse: { [warehouse: string]: number | null };
  sold: number;
}

interface RestockEvent {
  date: string;
  quantity: number;
  warehouse: string;
  reason: string | null;
  supplier: string | null;
}

interface InventoryKpis {
  currentStock: number;
  currentByWarehouse: { warehouse: string; quantity: number }[];
  unitsAdded: number;
  unitsSold: number;
  sellThroughRate: number | null;
  avgDailySales: number;
  daysOfStock: number | null;
  projectedStockoutDate: string | null;
}

interface InventoryAnalyticsResponse {
  series: InventorySeriesPoint[];
  warehouses: string[];
  restockEvents: RestockEvent[];
  kpis: InventoryKpis;
  start_date: string;
  end_date: string;
}

interface AnalyticsInventoryDashboardProps {
  sku: string;
  startDate: string;
  endDate: string;
}

const WAREHOUSE_COLORS = ['#E07856', '#4FA37C', '#8E6FC9', '#E3B341', '#56A6C9', '#D8689B', '#7A9E5F', '#7B8794'];

const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);

const formatDateLabel = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const accentGradient = (context: ScriptableContext<'line'>) => {
  const { ctx, chartArea } = context.chart;
  if (!chartArea) return 'rgba(59, 125, 216, 0.08)';
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, 'rgba(59, 125, 216, 0.16)');
  gradient.addColorStop(1, 'rgba(59, 125, 216, 0)');
  return gradient;
};

const AnalyticsInventoryDashboard: React.FC<AnalyticsInventoryDashboardProps> = ({ sku, startDate, endDate }) => {
  const [data, setData] = useState<InventoryAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWarehouses, setShowWarehouses] = useState(true);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate || !sku) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/inventory-analytics', {
        params: { sku, start_date: startDate, end_date: endDate },
      });
      setData(response.data);
    } catch (err: any) {
      console.error('Inventory analytics fetch error:', err);
      setError(err.response?.data?.error || 'Failed to fetch inventory analytics data');
    } finally {
      setLoading(false);
    }
  }, [sku, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!sku) {
    return (
      <div className="analytics-card">
        <p className="analytics-empty-text">Select a product or variation above to see its inventory analytics.</p>
      </div>
    );
  }
  if (loading) return <div className="analytics-card"><LoadingSpinner /></div>;
  if (error) return <div className="analytics-error-text">Error: {error}</div>;
  if (!data) return null;

  const { series, warehouses, restockEvents, kpis } = data;
  const labels = series.map((d) => d.date);

  // Restock markers: aggregate events per day, plotted on the total stock line
  const restocksByDate = restockEvents.reduce<{ [date: string]: RestockEvent[] }>((map, event) => {
    (map[event.date] = map[event.date] || []).push(event);
    return map;
  }, {});
  const restockPointData = series.map((d) =>
    restocksByDate[d.date] && d.total !== null ? d.total : null
  );

  // One colour per warehouse name, shared by the chart and the bar list. The
  // two lists are ordered differently (first seen vs. by quantity), so picking
  // by position in each gave the same warehouse different colours.
  const allWarehouseNames = Array.from(
    new Set([...warehouses, ...kpis.currentByWarehouse.map((w) => w.warehouse)])
  ).sort((a, b) => a.localeCompare(b));
  const warehouseColor = (name: string) =>
    WAREHOUSE_COLORS[Math.max(0, allWarehouseNames.indexOf(name)) % WAREHOUSE_COLORS.length];

  // Only chart warehouses that ever held stock in the period
  const activeWarehouses = warehouses.filter((w) =>
    series.some((d) => (d.byWarehouse[w] ?? 0) > 0)
  );

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Total stock',
        data: series.map((d) => d.total),
        borderColor: '#3B7DD8',
        borderWidth: 2.5,
        backgroundColor: accentGradient,
        fill: true,
        pointStyle: 'line' as const,
        pointRadius: 0,
        tension: 0.25,
        spanGaps: true,
        order: 2,
      },
      ...(showWarehouses
        ? activeWarehouses.map((w) => ({
            label: w,
            data: series.map((d) => d.byWarehouse[w]),
            borderColor: warehouseColor(w),
            borderWidth: 1.5,
            backgroundColor: 'rgba(0,0,0,0)',
            fill: false,
            pointStyle: 'line' as const,
            pointRadius: 0,
            tension: 0.25,
            spanGaps: true,
            order: 3,
          }))
        : []),
      {
        label: 'Units sold / day',
        data: series.map((d) => d.sold),
        borderColor: '#C0392B',
        borderWidth: 1.5,
        borderDash: [4, 3],
        backgroundColor: 'rgba(0,0,0,0)',
        fill: false,
        pointStyle: 'line' as const,
        pointRadius: 0,
        tension: 0,
        order: 4,
      },
      {
        label: 'Restock',
        data: restockPointData,
        borderColor: '#2E7D52',
        backgroundColor: '#2E7D52',
        showLine: false,
        pointStyle: 'triangle' as const,
        pointRadius: 7,
        pointHoverRadius: 9,
        order: 1,
      },
    ],
  };

  const kpiCards: { label: string; value: string; sub?: string }[] = [
    {
      label: 'Current Stock',
      value: formatNumber(kpis.currentStock),
      sub: 'units across all warehouses',
    },
    {
      label: 'Units Restocked',
      value: formatNumber(kpis.unitsAdded),
      sub: `${restockEvents.length} restock event${restockEvents.length === 1 ? '' : 's'} in period`,
    },
    {
      label: 'Units Sold',
      value: formatNumber(kpis.unitsSold),
      sub: `≈ ${kpis.avgDailySales.toFixed(1)} / day`,
    },
    {
      label: 'Sell-Through Rate',
      value: kpis.sellThroughRate !== null ? `${kpis.sellThroughRate.toFixed(1)}%` : '—',
      sub: 'sold ÷ (starting stock + restocks)',
    },
    {
      label: 'Days of Stock Left',
      value: kpis.daysOfStock !== null ? formatNumber(Math.round(kpis.daysOfStock)) : '—',
      sub: kpis.projectedStockoutDate
        ? `Projected stockout ${formatDateLabel(kpis.projectedStockoutDate)}`
        : 'No recent sales to project from',
    },
  ];

  const maxWarehouseQty = Math.max(1, ...kpis.currentByWarehouse.map((w) => w.quantity));

  return (
    <>
      <div className="analytics-kpi-grid">
        {kpiCards.map((kpi) => (
          <div className="analytics-kpi-card" key={kpi.label}>
            <span className="analytics-kpi-label">{kpi.label}</span>
            <span className="analytics-kpi-value">{kpi.value}</span>
            {kpi.sub && <span className="analytics-kpi-sub">{kpi.sub}</span>}
          </div>
        ))}
      </div>

      <div className="analytics-card">
        <div className="analytics-card-header">
          <h3>Stock Level Over Time</h3>
          <label className="analytics-toggle-label">
            <input
              type="checkbox"
              checked={showWarehouses}
              onChange={(e) => setShowWarehouses(e.target.checked)}
            />
            Show warehouse breakdown
          </label>
        </div>
        <ErrorBoundary>
          {series.some((d) => d.total !== null) ? (
            <div style={{ minHeight: '400px' }}>
              <Line
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: { mode: 'index', intersect: false },
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        font: { family: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif", size: 13 },
                        color: '#5A5A56',
                        padding: 20,
                        usePointStyle: true,
                      },
                    },
                    tooltip: {
                      backgroundColor: '#ffffff',
                      titleColor: '#1C1C1A',
                      bodyColor: '#1C1C1A',
                      borderColor: '#E8E8E4',
                      borderWidth: 1,
                      cornerRadius: 10,
                      padding: 12,
                      boxPadding: 4,
                      filter: (item: TooltipItem<'line'>) => item.raw !== null,
                      callbacks: {
                        label: (context: TooltipItem<'line'>) => {
                          if (context.dataset.label === 'Restock') {
                            const events = restocksByDate[labels[context.dataIndex]] || [];
                            return events.map(
                              (e) =>
                                `Restock +${formatNumber(e.quantity)} @ ${e.warehouse}` +
                                (e.supplier ? ` (${e.supplier})` : '') +
                                (e.reason ? ` — ${e.reason}` : '')
                            );
                          }
                          return `${context.dataset.label}: ${formatNumber(Number(context.raw) || 0)} units`;
                        },
                      },
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      border: { display: false },
                      grid: { color: 'rgba(0, 0, 0, 0.05)' },
                      ticks: { color: '#8F8F8A', font: { family: "'DM Mono', monospace", size: 11 } },
                      title: {
                        display: true,
                        text: 'Units in Stock',
                        color: '#5A5A56',
                        font: { family: "'DM Sans', sans-serif", size: 12 },
                      },
                    },
                    x: {
                      border: { display: false },
                      grid: { display: false },
                      ticks: {
                        color: '#8F8F8A',
                        font: { family: "'DM Sans', sans-serif", size: 11 },
                        maxRotation: 45,
                        autoSkip: true,
                        maxTicksLimit: 20,
                      },
                    },
                  },
                }}
              />
            </div>
          ) : (
            <p className="analytics-empty-text">
              No stock level history recorded for this selection in the chosen period.
            </p>
          )}
        </ErrorBoundary>
      </div>

      <div className="analytics-card">
        <div className="analytics-card-header">
          <h3>Current Stock by Warehouse</h3>
        </div>
        {kpis.currentByWarehouse.length > 0 ? (
          <div className="analytics-warehouse-list">
            {kpis.currentByWarehouse.map((w) => (
              <div className="analytics-warehouse-row" key={w.warehouse}>
                <span className="analytics-warehouse-name">{w.warehouse}</span>
                <div className="analytics-warehouse-bar-track">
                  <div
                    className="analytics-warehouse-bar"
                    style={{
                      width: `${(w.quantity / maxWarehouseQty) * 100}%`,
                      backgroundColor: warehouseColor(w.warehouse),
                    }}
                  />
                </div>
                <span className="analytics-warehouse-qty">{formatNumber(w.quantity)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="analytics-empty-text">No warehouse stock recorded for this selection.</p>
        )}
      </div>
    </>
  );
};

export default AnalyticsInventoryDashboard;
